import { HTMLAttributes, ReactNode, Children, ReactElement } from 'react';

import { Stack, Typography } from '@mui/material';
import scssVariables from '@styles/variables.module.scss';
import cx from 'classnames';

import styles from './PageSection.module.scss';

export interface PageSectionProps extends HTMLAttributes<HTMLDivElement> {
  title?: string;
  inlineSubtitle?: string;
  children: ReactNode;
  filled?: boolean;
  titleComponentType?:
    | 'span'
    | 'p'
    | 'legend'
    | 'label'
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'h5'
    | 'h6';
}

interface SectionInfoComponentProps {
  children: ReactNode;
  className?: string;
}

const SectionInfoComponent = ({
  children,
  className,
}: SectionInfoComponentProps) => {
  return <div className={className}>{children}</div>;
};

SectionInfoComponent.displayName = 'SectionInfo';

export const PageSection = ({
  title,
  inlineSubtitle,
  children,
  filled,
  titleComponentType = 'h2',
  ...rest
}: PageSectionProps) => {
  // Separate SectionInfo children from regular children
  const sectionInfoChildren: ReactNode[] = [];
  const regularChildren: ReactNode[] = [];

  Children.forEach(children, (child) => {
    if (
      typeof child === 'object' &&
      child !== null &&
      'type' in child &&
      (child as ReactElement).type === SectionInfoComponent
    ) {
      sectionInfoChildren.push(child);
    } else {
      regularChildren.push(child);
    }
  });

  return (
    <div
      className={cx(styles.PageSection, { [styles.PageSectionFilled]: filled })}
      {...rest}
    >
      <Stack direction="column" gap={2}>
        <Stack direction={'row'} justifyContent="space-between" gap={2}>
          {(title || inlineSubtitle) && (
            <Stack direction="row" alignItems="center" gap={2}>
              {title && (
                <Typography
                  className={styles.TitleWrapper}
                  variant="h4"
                  component={titleComponentType}
                >
                  {title}
                </Typography>
              )}
              {inlineSubtitle && (
                <Typography
                  variant="label"
                  component="span"
                  color={scssVariables.ColorFog100}
                >
                  {inlineSubtitle}
                </Typography>
              )}
            </Stack>
          )}
          {sectionInfoChildren}
        </Stack>
        <Stack gap={2}>{regularChildren}</Stack>
      </Stack>
    </div>
  );
};

// Attach the compound component
PageSection.Info = SectionInfoComponent;
